import type { Episode } from "./types";

const MANIFEST_URL = "/data/episodes.json";

let manifestPromise: Promise<Episode[]> | null = null;
let mapPromise: Promise<Map<string, Episode>> | null = null;

export function getEpisodeList(): Promise<Episode[]> {
  if (!manifestPromise) {
    manifestPromise = fetch(MANIFEST_URL).then((r) => {
      if (!r.ok) throw new Error(`failed to fetch ${MANIFEST_URL}: ${r.status}`);
      return r.json() as Promise<Episode[]>;
    });
  }
  return manifestPromise;
}

export function getEpisodes(): Promise<Map<string, Episode>> {
  if (!mapPromise) {
    mapPromise = getEpisodeList().then((arr) => new Map(arr.map((e) => [e.slug, e])));
  }
  return mapPromise;
}

export async function getEpisode(slug: string): Promise<Episode | undefined> {
  return (await getEpisodes()).get(slug);
}

/** First N episodes in newest-first order — manifest is already sorted. */
export async function getLatestEpisodes(n: number): Promise<Episode[]> {
  const list = await getEpisodeList();
  return list.slice(0, n);
}

export interface PulledQuote {
  text: string;
  episodeSlug: string;
  episodeTitle: string;
}

/** Flatten all per-episode quotes into a single shuffled list. */
export async function getShuffledQuotes(): Promise<PulledQuote[]> {
  const eps = await getEpisodeList();
  const all: PulledQuote[] = [];
  for (const ep of eps) {
    for (const q of ep.quotes ?? []) {
      all.push({ text: q, episodeSlug: ep.slug, episodeTitle: ep.title });
    }
  }
  // Fisher-Yates shuffle.
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [all[i], all[j]] = [all[j], all[i]];
  }
  return all;
}
